import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const VAPID_PUBLIC_KEY = 'BHQx9jjQgPWiUSnDNYzrHqLX_1lB3wsb8dc-J8sR1qW90-54AfARKsE-apVSOPKvQEwxb14yuUfB68TKBsLHBQU';

// Safe check — Notification API not available on all browsers
const getPermission = () => {
  try {
    return typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  } catch(e) { return 'denied'; }
};

const isSupported = () => {
  return typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;
};

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

// Save subscription with the user's REAL role and project from their profile
const saveSubscription = async (sub) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('role, project_id')
      .eq('id', user.id)
      .single();
    await supabase.from('push_subscriptions').upsert({
      user_id: user.id,
      role: profile?.role || 'client',
      project_id: profile?.project_id || null,
      subscription: sub.toJSON(),
    }, { onConflict: 'user_id' });
  } catch(e) {
    console.log('Save subscription skipped:', e.message);
  }
};

export function usePushNotifications() {
  const [permission, setPermission] = useState(() => getPermission());
  const [subscription, setSubscription] = useState(null);

  // Clear the icon badge whenever the app is opened or comes to the foreground
  useEffect(() => {
    const clear = async () => {
      try { navigator.clearAppBadge?.(); } catch(e) {}
      try {
        const c = await caches.open('mh-badge');
        await c.put('count', new Response('0'));
      } catch(e) {}
    };
    clear();
    const onVis = () => { if (document.visibilityState === 'visible') clear(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    if (!isSupported()) return;
    navigator.serviceWorker.register('/sw.js')
      .then(async (reg) => {
        let sub = await reg.pushManager.getSubscription();
        // If the existing subscription was created with an old VAPID key, replace it
        if (sub && sub.options?.applicationServerKey) {
          const current = new Uint8Array(sub.options.applicationServerKey);
          const expected = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
          const same = current.length === expected.length && current.every((b, i) => b === expected[i]);
          if (!same) {
            try { await sub.unsubscribe(); } catch(e) {}
            sub = null;
          }
        }
        // Permission granted but subscription lost (e.g. site data cleared) — resubscribe automatically
        if (!sub && getPermission() === 'granted') {
          try {
            sub = await reg.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
          } catch(e) { console.log('Auto-resubscribe failed:', e.message); }
        }
        if (sub) {
          setSubscription(sub);
          setPermission('granted');
          saveSubscription(sub); // re-save so role/project stay correct
        }
      })
      .catch(e => console.log('SW registration skipped:', e.message));
  }, []);

  const requestPermission = async () => {
    if (!isSupported()) return null;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== 'granted') return null;
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
      setSubscription(sub);
      await saveSubscription(sub);
      return sub;
    } catch(e) {
      console.error('Push subscribe error:', e);
      return null;
    }
  };

  const sendTestNotification = async () => {
    let sub = subscription;
    if (!sub) {
      sub = await requestPermission();
      if (!sub) return;
    }
    await sendPushNotification(
      sub,
      '🏠 Muller Homes',
      'Notifications are working! Tap to open.',
      window.location.origin
    );
  };

  return { permission, subscription, requestPermission, sendTestNotification };
}

// Send push via Vercel serverless function
export async function sendPushNotification(subscription, title, body, url) {
  try {
    const res = await fetch('/api/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscription, title, body, url }),
    });
    if (!res.ok) {
      const err = await res.text();
      console.error('Push API error:', err);
    }
    return res.ok;
  } catch(e) {
    console.error('Send push error:', e);
    return false;
  }
}

// Send push to all client subscriptions for a project
export async function notifyProjectClients(projectId, title, body, url) {
  try {
    await supabase.from('app_notifications').insert({
      project_id: parseInt(projectId), recipient_role: 'client', title, body: body || '', url: url || null,
    });
  } catch(e) {}
  try {
    const { data: subs } = await supabase
      .from('push_subscriptions')
      .select('subscription')
      .eq('project_id', projectId)
      .eq('role', 'client');
    if (!subs?.length) return 0;
    let sent = 0;
    for (const row of subs) {
      const ok = await sendPushNotification(row.subscription, title, body, url);
      if (ok) sent++;
    }
    return sent;
  } catch(e) {
    console.error('notifyProjectClients error:', e);
    return 0;
  }
}

// Send push to the builder (and assistant) — used when clients act
export async function notifyBuilder(title, body, url) {
  try {
    const pid = parseInt((url || '').match(/[?&]project=(\d+)/)?.[1] || '0') || null;
    await supabase.from('app_notifications').insert({
      project_id: pid, recipient_role: 'builder', title, body: body || '', url: url || null,
    });
  } catch(e) {}
  try {
    const { data: subs } = await supabase
      .from('push_subscriptions')
      .select('subscription')
      .in('role', ['builder', 'assistant']);
    if (!subs?.length) return 0;
    let sent = 0;
    for (const row of subs) {
      const ok = await sendPushNotification(row.subscription, title, body, url);
      if (ok) sent++;
    }
    return sent;
  } catch(e) {
    console.error('notifyBuilder error:', e);
    return 0;
  }
}
